import { useEffect, useMemo, useState } from 'react';
import { Share2, Printer, Copy, ExternalLink, Check } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
}

/** Page-aware share payload: title from document.title, text from the page's
 *  meta description (set per-page via Helmet) so the OS share sheet carries
 *  the actual content of the page — hospital name, scenario, prices — instead
 *  of a generic blurb. */
function buildSharePayload() {
  const title = document.title || 'American Hospital Prices';
  const meta = document.querySelector('meta[name="description"]');
  const text = meta?.getAttribute('content') || 'Real hospital prices, from each hospital’s own published chargemaster.';
  return { title, text, url: window.location.href };
}

export const ContextMenu = ({ x, y, onClose }: ContextMenuProps) => {
  const [position, setPosition] = useState({ x, y });
  const [copied, setCopied] = useState(false);
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  useEffect(() => {
    // Adjust position if menu would go off screen
    const menuWidth = 200;
    const menuHeight = 200;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let adjustedX = x;
    let adjustedY = y;

    if (x + menuWidth > windowWidth) adjustedX = windowWidth - menuWidth - 10;
    if (y + menuHeight > windowHeight) adjustedY = windowHeight - menuHeight - 10;

    setPosition({ x: adjustedX, y: adjustedY });
  }, [x, y]);

  // Theme: AHP gets patriotic light/dark surfaces; Luigi's keeps matrix green.
  // (The old hardcoded black + matrix-green + text-muted-foreground combo was
  // unreadable dark-on-dark in AHP dark mode.)
  const T = useMemo(() => {
    if (!isAhp) {
      return {
        bg: 'rgba(0,0,0,0.95)', border: 'rgba(0,255,65,0.3)',
        text: '#00FF41', textMuted: 'rgba(0,255,65,0.65)',
        hover: 'rgba(0,255,65,0.10)', divider: 'rgba(0,255,65,0.2)',
        font: 'monospace',
      };
    }
    return isDark
      ? {
          bg: 'rgba(13,30,53,0.97)', border: 'rgba(197,165,90,0.35)',
          text: '#FAF9F6', textMuted: 'rgba(250,249,246,0.72)',
          hover: 'rgba(197,165,90,0.14)', divider: 'rgba(250,249,246,0.12)',
          font: "'Instrument Sans', system-ui, sans-serif",
        }
      : {
          bg: 'rgba(255,255,255,0.98)', border: 'rgba(0,40,104,0.18)',
          text: '#1B1B1B', textMuted: 'rgba(27,27,27,0.65)',
          hover: 'rgba(0,40,104,0.06)', divider: 'rgba(27,27,27,0.10)',
          font: "'Instrument Sans', system-ui, sans-serif",
        };
  }, [isAhp, isDark]);

  const handleShare = async () => {
    const payload = buildSharePayload();
    if (navigator.share) {
      try {
        await navigator.share(payload);
      } catch {
        /* user cancelled — nothing to do */
      }
    } else {
      try {
        await navigator.clipboard.writeText(payload.url);
        toast.success('Link copied to clipboard');
      } catch {
        toast.error("Couldn't copy the link");
      }
    }
    onClose();
  };

  const handlePrint = () => {
    window.print();
    onClose();
  };

  const handleCopyURL = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(onClose, 400);
    } catch {
      toast.error("Couldn't copy the link");
      onClose();
    }
  };

  const handleOpenInNewTab = () => {
    window.open(window.location.href, '_blank');
    onClose();
  };

  const itemStyle: React.CSSProperties = {
    width: '100%', padding: '10px 16px', display: 'flex', alignItems: 'center',
    gap: 12, textAlign: 'left', fontSize: 13, color: T.text,
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: T.font, transition: 'background 0.12s ease',
  };
  const mutedItemStyle: React.CSSProperties = { ...itemStyle, color: T.textMuted };
  const hoverOn = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = T.hover; };
  const hoverOff = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = 'none'; };

  return (
    <>
      {/* Backdrop to close menu when clicking outside. Above the native tab
          bar (z-10000+) so bottom-of-screen menus aren't covered. */}
      <div
        className="fixed inset-0 z-[10070]"
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      />

      {/* Context Menu */}
      <div
        role="menu"
        className="fixed z-[10071] w-[200px] rounded-lg shadow-xl overflow-hidden backdrop-blur-sm"
        style={{
          left: `${position.x}px`, top: `${position.y}px`,
          background: T.bg, border: `1px solid ${T.border}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '4px 0' }}>
          <button role="menuitem" onClick={handleShare} style={itemStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
            <Share2 style={{ width: 16, height: 16 }} />
            <span>Share this page</span>
          </button>

          <button role="menuitem" onClick={handlePrint} style={itemStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
            <Printer style={{ width: 16, height: 16 }} />
            <span>Print</span>
          </button>

          <div style={{ borderTop: `1px solid ${T.divider}`, margin: '4px 0' }} />

          <button role="menuitem" onClick={handleCopyURL} style={mutedItemStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
            {copied ? <Check style={{ width: 16, height: 16, color: '#16a34a' }} /> : <Copy style={{ width: 16, height: 16 }} />}
            <span>{copied ? 'Copied!' : 'Copy link'}</span>
          </button>

          <button role="menuitem" onClick={handleOpenInNewTab} style={mutedItemStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
            <ExternalLink style={{ width: 16, height: 16 }} />
            <span>Open in new tab</span>
          </button>
        </div>
      </div>
    </>
  );
};
